<template>
  <div :class="['retrospective-container', containerClass]">
    <div class="report-card">
      <!-- Header Section -->
      <header class="header">
        <div class="year-badge">
          <h1 class="year">{{ reportData.year }}</h1>
          <div class="subtitle-bg">
            <span class="subtitle">retrospectiva <img src="@/assets/img/icone-branco.png" alt="Logo" class="arrow" /></span>
          </div>
        </div>
        
        <h2 class="main-title">Seu ano com a gente</h2>
        
        <p class="intro-text">
          Iniciamos mais um ano de parceria e, para seguir avançando, é essencial compreender o caminho percorrido até aqui. 
          Essa retrospectiva traz um panorama dos principais resultados e indicadores dos seus processos conosco.
        </p>
      </header>

      <div class="separator"></div>

      <!-- KPI Section -->
      <section class="kpi-section">
        <div class="kpi-card">
          <div class="kpi-value">{{ reportData.processosAbertos }}</div>
          <div class="kpi-label">Processos Abertos</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ reportData.processosRegistrados }}</div>
          <div class="kpi-label">Processos Registrados</div>
        </div>
      </section>

      <div class="separator"></div>

      <!-- Charts Section -->
      <section class="charts-section">
        <!-- Chart 1: Canais -->
        <div class="chart-column">
          <h3 class="chart-title">Canais de Parametrização</h3>
		  <retrospect-chart :data="canaisLiberacao" :size="170"  legend-position="external"/>
        </div>

        <!-- Chart 2: Período -->
        <div class="chart-column">
          <h3 class="chart-title">Período de Liberação</h3>
		  <retrospect-chart :data="periodoArmazenagem" :size="170"  legend-position="external"/>
        </div>
      </section>

      <div class="separator-thin"></div>

      <!-- Lead Time Section -->
      <section class="lead-time-section">
        <h3 class="section-title">Lead Time</h3>
        <div class="cards-row">
          <div class="info-card">
            <div class="card-header">Registro DI/DUIMP</div>
            <div class="card-body">
              <span class="big-number">{{ reportData.registroDeDI }}</span>
              <span class="unit">dias</span>
            </div>
          </div>
          <div class="info-card">
            <div class="card-header">Carregamento</div>
            <div class="card-body">
              <span class="big-number">{{ reportData.carregamento }}</span>
              <span class="unit">dias</span>
            </div>
          </div>
          <div class="info-card">
            <div class="card-header">Entrega</div>
            <div class="card-body">
              <span class="big-number">{{ reportData.entrega }}</span>
              <span class="unit">dias</span>
            </div>
          </div>
          <div class="info-card">
            <div class="card-header">Encerramento</div>
            <div class="card-body">
              <span class="big-number">{{ reportData.encerramento }}</span>
              <span class="unit">dias</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Finance Section -->
      <section class="finance-section">
        <div class="dollar-badge">
          <div class="badge-label">Cotação média do dólar no ano</div>
          <div class="badge-value">{{ formatCurrency(reportData.dolarMedio, 2, 2) }}</div>
        </div>

        <p class="freight-label">Frete médio dos embarques FCL nas principais origens</p>

        <div class="flags-row">
          <div class="flag-item">
			<div class="flag-circle-container">
				<span class="flag-circle">
				<img src="@/assets/img/flags/china.jpg" alt="China" />
				</span>
			</div>
            <div class="flag-value">{{ formatCurrency(freteChina, 2, 2) }}</div>
          </div>
          <div class="flag-item">
			<div class="flag-circle-container">
				<span class="flag-circle">
					<img src="@/assets/img/flags/estados-unidos.jpg" alt="USA" />
				</span>
			</div>
            <div class="flag-value">{{ formatCurrency(freteEstadosUnidos, 2, 2) }}</div>
          </div>
          <div class="flag-item">
			<div class="flag-circle-container">
				<span class="flag-circle">
					<img src="@/assets/img/flags/uniao-europeia.jpg" alt="EU" />
				</span>
			</div>
            <div class="flag-value">{{ formatCurrency(freteUniaoEuropeia, 2, 2) }}</div>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-bar">
          Conte conosco para mais um ano de muitas importações e sucesso!
        </div>
        <div class="logo-area">
          <img src="@/assets/img/logo-horizontal-azul.png" alt="Prime Internacional" class="footer-logo" />
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import '@/assets/css/customFonts.css';
import RetrospectChart from '@/components/RetrospectChart';
export default {
  name: 'AnnualReport',
  components: { RetrospectChart },
  props: {
    containerClass: {
      type: String,
      default: 'exportar-pdf',
    },
    reportData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
    };
  },
  computed: {
    periodoArmazenagem() {
      const periodoArmazenagem = this.reportData.periodoDeArmazenagem;
      const data = [];

      if (!periodoArmazenagem) return [];

      for (let i = 0; i < Object.keys(periodoArmazenagem).length; i += 1) {
        const [key, val] = Object.entries(periodoArmazenagem)[i];

        if (val > 0 && key !== 'total') {
          data.push({
			key: key,
            label: this.returnNameFormatted(key),
            value: val,
			color: this.getColorPeriodoArmazenagem(key),
          });
        }
      }
      return data;
    },
    freteChina() {
      if (!this.reportData.frete) return 0;
      const item = this.reportData.frete.find((data) => data.country === 'China');
      return item ? item.value : 0;
    },
    freteEstadosUnidos() {
      if (!this.reportData.frete) return 0;
      const item = this.reportData.frete.find((data) => data.country === 'Estados Unidos');
      return item ? item.value : 0;
    },
    freteUniaoEuropeia() {
      if (!this.reportData.frete) return 0;
      const item = this.reportData.frete.find((data) => data.country === 'União Europeia');
      return item ? item.value : 0;
    },
    canaisLiberacao() {
      const canaisLiberacao = this.reportData.canaisDeLiberacao;
      const data = [];

      if (!canaisLiberacao) return [];

      for (let i = 0; i < Object.keys(canaisLiberacao).length; i += 1) {
        const [key, val] = Object.entries(canaisLiberacao)[i];

        if (val > 0 && key !== 'total') {
          data.push({
			key: key,
            label: this.returnNameFormatted(key),
            value: val,
			color: this.getColorChannels(key),
          });
        }
      }
      return data;
    },
  },
  methods: {
    formatCurrency(value, minimumFractionDigits = 3, maximumFractionDigits = 4) {
      if (value === undefined || value === null) return '';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits,
      }).format(value);
    },
    getColorChannels(name) {
      switch (name) {
        case 'VERDE':
          return '#93C48E';
        case 'AMARELO':
          return '#FDDF94';
        case 'VERMELHO':
          return '#BB082F';
        case 'CINZA':
          return '#a6a4a4';
      }
    },
    getColorPeriodoArmazenagem(name) {
      switch (name) {
        case 'PRIMEIRO_PERIODO':
          return '#113882';
        case 'SEGUNDO_PERIODO':
          return '#2D61C2';
        case 'TERCEIRO_PERIODO':
          return '#5E8DE4';
        case 'QUARTO_PERIODO':
          return '#92B8FF';	
      }
    },
    isChannel(name) {
      if (name === 'VERDE' || name === 'AMARELO' || name === 'VERMELHO') {
        return true;
      }

      return false;
    },
    returnNameFormatted(name) {
      switch (name) {
        case 'total':
          return 'Total';

        case 'PRIMEIRO_PERIODO':
          return 'Primeiro período';

        case 'SEGUNDO_PERIODO':
          return 'Segundo período';

        case 'TERCEIRO_PERIODO':
          return 'Terceiro período';

        case 'QUARTO_PERIODO':
          return 'Quarto período';

        case 'OUTROS':
          return 'Outros';

        case 'VERDE':
          return 'Canal Verde';

        case 'AMARELO':
          return 'Canal Amarelo';

        case 'VERMELHO':
          return 'Canal Vermelho';

        default:
          return 'Sem nome';
      }
    },
  },
  mounted() {
  },
};
</script>

<style scoped>
  .retrospective-container {
    background-color: #f5f5f5;
    display: flex;
    justify-content: center;
    font-family: 'Pluto', sans-serif;
    font-weight: 200; /* Pluto Leve para textos */
    color: #555;
    /* Maintain min-width or width control if necessary for PDF export */
    width: 940px;
    margin: 0 auto;
    box-sizing: border-box;
  }
  
  .report-card {
    background: white;
    width: 100%;
    padding: 80px 10px 40px 10px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
  }
  
  /* Header */
  .header {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .year-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }
  
  .year {
    font-family: 'Tan-Nimbus', sans-serif;
    font-weight: 800; /* Pluto Extranegrito para números */
    font-size: 7rem;
    line-height: 0.5;
    color: var(--navy-blue);
    margin: 0;
    position: relative;
    z-index: 1;
  }
  
  .subtitle-bg {
    background-color: var(--navy-blue);
    padding: 0px 75px;
    border-radius: 50px;
    position: relative;
    z-index: 2;
    text-align: center;
  }
  
  .subtitle {
    color: white;
    text-transform: lowercase;
    font-weight: 200; /* Pluto Leve */
    font-size: 1.4rem;
    letter-spacing: 7px;
    font-family: 'Pluto', sans-serif;
  }
  
  .subtitle .arrow {
    height: 1.7rem;
    width: auto;
    vertical-align: middle;
  }
  
  .main-title {
    font-family: 'Tan-Nimbus', sans-serif;
    font-weight: 200; /* Pluto Leve */
    font-style: normal;
    font-size: 2.2rem;
    color: var(--gold);
    margin: 15px 0 20px 0;
    line-height: 1.2;
  }
  
  .intro-text {
    font-family: 'Pluto', sans-serif;
    text-align: center;
    font-size: 0.95rem;
    font-weight: 200; /* Pluto Leve */
    line-height: 1.5;
    color: #777;
    max-width: 80%;
    margin: 0 auto;
  }
  
  /* Variables for colors */
  /* Scoped styles don't support :root well for component variables usually, but works in some setups. 
     Better to apply to container or use specific class. Using .retrospective-container for scope vars if supported, 
     or just declaring them directly in rules if :root fails. Sticking to :root equivalent pattern or direct usage.
     For safety in scoped CSS, I will define variables on the container class. */
  .retrospective-container {
    --navy-blue: #1C3C70;
    --gold: #E0B350;
    --grey: #656766;
    --light-yellow: #fddf94;
  }
  
  /* Separator */
  .separator {
    width: 100%;
    height: 2px;
    background-color: var(--navy-blue);
    border: none;
    margin: 15px 0 30px 0;
    opacity: 1;
  }
  
  .separator-thin {
    width: 100%;
    height: 6px;
    background-color: transparent;
    border: 2px solid var(--light-yellow);
    border-radius: 10px;
    box-sizing: border-box;
  }

  /* KPI Section */
  .kpi-section {
    display: flex;
    justify-content: center;
    gap: 40px;
    width: 100%;
    padding: 2px 0;
  }

  .kpi-card {
    border: 2px solid var(--gold);
    border-radius: 50px;
    padding: 10px 40px 5px 40px;
    text-align: center;
    min-width: 250px;
    position: relative;
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .kpi-value {
    font-family: 'Pluto', sans-serif;
    font-size: 3rem;
    font-weight: 600; /* Pluto Extranegrito para números */
    color: var(--gold);
    line-height: 1;
    background: white;
    margin-top: -34px;
    padding: 0 30px;
    display: inline-block;
  }

  .kpi-label {
    font-family: 'Pluto', sans-serif;
    font-size: 1rem;
    font-weight: 200; /* Pluto Leve */
    color: var(--grey);
    text-transform: capitalize;
  }
  
  /* Charts Section */
  .charts-section {
    display: flex;
	justify-content: center;
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
  }
  
  .chart-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 45%;
  }
  
  .chart-title {
    font-family: 'Pluto', sans-serif;
    font-size: 1.1rem;
    color: var(--grey);
    font-weight: 200; /* Pluto Leve */
    margin-top: 0px !important;
	margin-bottom: 0px !important;
  }
  
  .chart-container {
    position: relative;
    width: 300px; /* Increased for ApacheChart visibility */
    height: 300px;
  }
  
  /* Lead Time Section */
  .lead-time-section {
    width: 100%;
    text-align: center;
  }
  
  .section-title {
    font-family: 'Pluto', sans-serif;
    color: var(--navy-blue);
    font-size: 1.5rem;
    font-weight: bold; /* Bold como na imagem */
    margin-bottom: 20px;
	margin-top: 15px !important;
	text-transform: none !important;
  }
  
  .cards-row {
    display: flex;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
  }
  
  .info-card {
    width: 140px;
    border-radius: 20px;
    overflow: visible;
    border: 2px solid var(--navy-blue);
    background: white;
    display: flex;
    flex-direction: column;
  }
  
  .card-header {
    font-family: 'Pluto', sans-serif;
    background-color: var(--navy-blue);
    color: white;
    padding: 8px 2px;
    font-size: 0.9rem;
    font-weight: bold; /* Bold como na imagem */
    text-transform: none;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
  
  .card-body {
    padding: 10px 0;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 80px;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
  
  .big-number {
    font-family: 'Pluto', sans-serif;
    font-size: 2.5rem;
    font-weight: 600; /* Pluto Extranegrito para números */
    color: var(--navy-blue); /* Dark blue como na imagem */
    line-height: 1;
  }
  
  .unit {
    font-family: 'Pluto', sans-serif;
    font-size: 0.8rem;
    font-weight: 200; /* Pluto Leve */
    color: var(--navy-blue); /* Dark blue como na imagem */
    margin-top: -5px;
  }
  
  /* Finance Section */
  .finance-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
  }
  
  .dollar-badge {
    border: 2px solid var(--gold);
    border-radius: 60px;
    padding: 10px 0px 30px 0px;
    text-align: center;
    margin-bottom: 20px;
    position: relative;
    min-width: 360px;
  }
  
  .badge-label {
    font-family: 'Pluto', sans-serif;
    color: var(--grey);
    font-size: 1.2rem;
    font-weight: 200; /* Pluto Leve */
    position: static;
    transform: none;
    background: transparent;
  }
  
  .badge-value {
    font-family: 'Pluto', sans-serif;
    font-size: 2.3rem;
    font-weight: 600; /* Pluto Extranegrito para números */
    color: var(--gold);
    line-height: 1;
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 0 20px;
    white-space: nowrap;
  }
  
  .freight-label {
    font-family: 'Pluto', sans-serif;
    color: var(--grey);
    font-size: 1.1rem;
    font-weight: 200; /* Pluto Leve */
    margin-bottom: 0px !important;
    margin-top: 20px;
  }
  
  .flags-row {
    display: flex;
    justify-content: center;
    gap: 30px;
    width: 100%;
    /* margin-top: 30px; */
  }
  
  .flag-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    width: 220px;
  }
  
  .flag-circle {
    width: 45px;
    height: 45px;
    border-radius: 50%;
	overflow: hidden;
    top: 10px;
    z-index: 2;
    background: white;
    border: 5px solid white; 
    box-sizing: content-box;
    box-shadow: none;
    display: block;
    position: relative;
  }
  
  .flag-circle-container {
	padding: 0px 35px;
    position: relative;
    top: 5px;
    z-index: 2;
    border: 5px solid white;
  }
  
  .flag-circle img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .flag-value {
    font-family: 'Pluto', sans-serif;
    border: 1px solid var(--navy-blue);
    border-radius: 50px;
    padding: 10px 15px 5px 10px;
    width: 100%;
    text-align: center;
    color: var(--grey);
    font-size: 1.1rem;
    background: white;
    position: relative;
    z-index: 1;
  }
  
  /* Footer */
  .footer {
    width: 100%;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-bar {
    font-family: 'Pluto', sans-serif;
    background-color: var(--navy-blue);
    color: white;
    width: 83%;
    padding: 4px 0px;
    text-align: center;
    border-radius: 50px;
    font-size: 1.1rem;
    font-weight: 200; /* Pluto Leve */
    margin-bottom: 25px;
    box-sizing: border-box;
  }
  
  .footer-logo {
    height: 60px;
    width: auto;
  }
  
  /* Responsiveness */
  @media (max-width: 768px) {
    .report-card {
      padding: 20px;
    }
    
    .kpi-section {
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }
    
    .charts-section {
      flex-direction: column;
      align-items: center;
      gap: 40px;
    }
    
    .chart-column {
      width: 100%;
    }
    
    .cards-row {
      flex-wrap: wrap;
    }
    
    .info-card {
      min-width: 40%;
    }
  
    .flags-row {
      flex-direction: column;
      align-items: center;
    }
  }
</style>
